/**
 * Phí khám (exam registration fee). Real /api/v1/appointments/{id}/exam-fee.
 *
 * The fee row is auto-created at booking, so `get` always returns a value.
 * Contract is frozen — this client always hits the backend (no mock branch).
 */
import { apiClient } from "./client";

/**
 * `BHYT` is the legacy value kept so existing rows stay valid; a BHYT visit booked since #18834 is
 * written `BHYT_UNPRICED` — the gross fee IS recorded (so it is a visible suspense amount rather
 * than a silent loss) but nothing is collected at the counter, because the BHYT/patient split is
 * not decided yet (#18828/#18829).
 */
export type ExamFeeStatus =
  | "PENDING"
  | "PAID"
  | "WAIVED"
  | "BHYT"
  | "BHYT_UNPRICED"
  // #19854 — server statuses the screen had no word for, so the badge rendered empty. VOIDED is
  // written on cancel / no-show (now also nightly by the auto no-show job), COVERED when another
  // visit already paid this one's exam fee (department transfer).
  | "VOIDED"
  | "COVERED";
/**
 * What the cashier collected. Aligned with `payments.method` in #18850 — the exam fee and an
 * invoice payment are the two places a clinic's money lands, and they used to spell bank transfer
 * differently (`TRANSFER` here, `BANK_TRANSFER` there), so any report unioning them had to know
 * both. The backend still accepts the old `TRANSFER` and translates it, so an older deployed client
 * keeps working; new code should not send it.
 */
export type ExamFeePayMethod = "CASH" | "BANK_TRANSFER" | "CARD";

/**
 * How the patient reached this visit (#18857). It decides the mức hưởng that applies, which is not
 * always the one printed on the card.
 *
 * `null` means nobody has recorded it — a real state, distinct from "đúng tuyến", and the reason a
 * BHYT fee can sit unpriced with everything else in place.
 */
export type BhytCareContext = "CORRECT_ROUTE" | "REFERRED" | "SELF_REFERRED" | "OTHER";

export interface ExamFee {
  appointmentId: string;
  amount: number;
  status: ExamFeeStatus;
  serviceId: string | null;
  serviceName: string | null;
  paymentMethod: string | null;
  paidAt: string | null;
  /** Read live off the appointment — may have been corrected since the fee was priced (#18857). */
  careContext: BhytCareContext | null;
  /** The mức hưởng on the card. Shown beside, never instead of, the applied rate. */
  baseBenefitPercent: number | null;
  /** The mức hưởng actually applied. Null while the fee is BHYT_UNPRICED. */
  benefitPercent: number | null;
  /** Which rule produced it, or why none could. */
  benefitRuleCode: string | null;
}

export const examFeeApi = {
  /** Fetch the exam fee for an appointment. Always resolves (auto-created at booking). */
  get(appointmentId: string): Promise<ExamFee> {
    return apiClient
      .get<ExamFee>(`/v1/appointments/${appointmentId}/exam-fee`)
      .then((r) => r.data);
  },
  /** Collect the exam fee. Returns the updated fee (status PAID). */
  pay(appointmentId: string, method: ExamFeePayMethod): Promise<ExamFee> {
    return apiClient
      .post<ExamFee>(`/v1/appointments/${appointmentId}/exam-fee/pay`, { method })
      .then((r) => r.data);
  },
  /**
   * Record how the patient reached this visit, re-pricing the fee if it was waiting on exactly
   * that. A fee already PENDING or PAID keeps its amount — the context is recorded, the quote is
   * not silently moved (#18857).
   */
  setCareContext(appointmentId: string, careContext: BhytCareContext): Promise<ExamFee> {
    return apiClient
      .post<ExamFee>(`/v1/appointments/${appointmentId}/exam-fee/bhyt-care-context`, {
        careContext,
      })
      .then((r) => r.data);
  },
};

export const examFeeQueryKeys = {
  detail: (appointmentId: string) => ["exam-fee", appointmentId] as const,
};

export const examFeeStatusLabel: Record<ExamFeeStatus, string> = {
  PENDING: "Chưa thu",
  PAID: "Đã thu",
  WAIVED: "Miễn phí",
  BHYT: "BHYT chi trả",
  BHYT_UNPRICED: "BHYT — chưa phân bổ",
  VOIDED: "Đã huỷ",
  COVERED: "Đã thu ở lượt khác",
};

export const examFeePayMethodLabel: Record<ExamFeePayMethod, string> = {
  CASH: "Tiền mặt",
  BANK_TRANSFER: "Chuyển khoản",
  CARD: "Thẻ",
};

/**
 * Rows written before #18850 stored `TRANSFER` for what is now `BANK_TRANSFER`. Reading is where
 * that has to be tolerated — the value is already in the database and renaming it on screen would
 * make an old receipt unrecognisable.
 */
export const EXAM_FEE_LEGACY_PAY_METHOD: Record<string, ExamFeePayMethod> = {
  TRANSFER: "BANK_TRANSFER",
};

/** Wording reception uses out loud, not the statute's. */
export const bhytCareContextLabel: Record<BhytCareContext, string> = {
  CORRECT_ROUTE: "Đúng nơi đăng ký ban đầu",
  REFERRED: "Có giấy chuyển tuyến",
  SELF_REFERRED: "Tự đến (không chuyển tuyến)",
  OTHER: "Trường hợp khác",
};

export const BHYT_CARE_CONTEXTS: BhytCareContext[] = [
  "CORRECT_ROUTE",
  "REFERRED",
  "SELF_REFERRED",
  "OTHER",
];
